import React, { useState } from 'react'
import { ActivityDto, ActivityRowType, Units } from '../../../../services/api.types'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { t } from 'ttag'
import { getServiceProviderLabel } from '../../../../common/helpers/formHelpers'
import { accessibleDarkerGray, darkBlue, darkGray, primaryBlue } from '../../../../styles/material-ui-theme-v2'
import ActivityActions from '../../../../components/ActivityActions/ActivityActions'
import dayjs from '../../../../dayjsConfig'
import { currentLocale } from '../../../../i18nInit'
import { getWorkoutLabel, getActivityIconComponent } from '../../../../common/workoutActivities'
import RouteMap from '../../../../components/RouteMap/RouteMap'
import { ReactComponent as DropdownIcon } from '../../../../assets/v3/icons/notifications/dropdown.svg'
import RouteStatsChart from '../../../../components/RouteStatsChart/RouteStatsChart'
import { detectAvailableSeries } from '../../../../components/RouteStatsChart/routeSeries'
import { WorkoutType } from '../../../../plugins/ActivityTrackerPlugin'
import {
  formatDistance as formatDistanceWithUnits,
  formatSwimmingDistance as formatSwimmingDistanceWithUnits,
  formatPace as formatPaceWithUnits,
  formatSwimmingPace as formatSwimmingPaceWithUnits,
  formatSpeed as formatSpeedWithUnits,
  getDistanceUnit,
  getPaceUnit,
  getSpeedUnit,
  getSwimmingDistanceUnit,
  getSwimmingPaceUnit,
  formatElevation,
  getElevationUnit,
} from '../../../../common/helpers/unitFormatters'
import { useDispatch, useSelector } from 'react-redux'
import { IGlobalState } from '../../../../Root/store/appReducer'
import Loader from '../../../../components/Loader/Loader'
import { loadActivityRoute } from '../../store/ActivitiesActions'
import { getRoutesById, getRouteLoadingIds } from '../../store/ActivitiesSelectors'

const useStyles = makeStyles((theme) => ({
  activityItem: {
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${darkGray}`,
    backgroundColor: 'white',
    cursor: 'default',
    transition: 'box-shadow 150ms ease-in-out',
  },
  activityHeader: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '12px',
    justifyContent: 'space-between',
  },
  activityContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
  },
  activityTitle: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: darkBlue,
    margin: 0,
    lineHeight: '18px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  // Matches the inbox notification's second row (sender / sent-at line).
  activityMeta: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: darkBlue,
    opacity: 0.7,
    margin: 0,
    lineHeight: '14px',
    '&::first-letter': {
      textTransform: 'uppercase',
    },
  },
  activityStats: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px 24px',
    //margin: '16px 0 12px',
    margin: '16px 0 0',
    // Clips the divider of the first stat in each row (it hangs outside the left edge), so a
    // wrapped row never starts with a divider — and the previous row never ends with one.
    overflow: 'hidden',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    minWidth: 'fit-content',
    // Divider on the LEFT of every stat, halfway into the column gap. Row-leading dividers are
    // clipped by the container (see activityStats), which — unlike a right-side divider with a
    // :last-child exception — also handles the last item of a wrapped row.
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-12px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '1px',
      height: '40px',
      backgroundColor: darkGray,
    },
    '& .value': {
      fontSize: '18px',
      fontWeight: '700',
      color: darkBlue,
      lineHeight: 1.5,
      marginBottom: '4px',
    },
    '& .label': {
      fontSize: '12px',
      fontWeight: 400,
    },
  },
  // Visual treatment for inline unit suffixes (rendered as <abbr> so the full term is
  // exposed to screen readers and on hover). The browser's default dotted underline is
  // suppressed so they read as a regular subordinate label.
  unit: {
    fontSize: '12px',
    marginLeft: '2px',
    textDecoration: 'none',
    cursor: 'inherit',
  },
  reviewDuplicatesLink: {
    //marginTop: '8px',
    padding: 0,
    background: 'none',
    border: 'none',
    color: darkBlue,
    fontSize: '12px',
    flexShrink: 0,
    fontWeight: 600,
    fontFamily: 'inherit',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  // Nested list of multi-sport legs (swim / bike / run / transition) under a parent header card.
  legList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '4px',
  },
  legItem: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${darkGray}`,
    //backgroundColor: '#f7f8fa',
  },
  legHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  legTitle: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: darkBlue,
    margin: 0,
    lineHeight: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  // Bottom row of the card: the "Show more" toggle and, after a middot, the providing source
  // ("Garmin fenix 8 Pro") — moved out of the meta line, which got too cramped on mobile.
  showMoreRow: {
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    alignSelf: 'flex-start',
    minWidth: 0,
  },
  showMoreSource: {
    fontSize: '12px',
    color: accessibleDarkerGray,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // "Show more / Show less" toggle beneath the stats (and, when expanded, the chart and map).
  // Styled after the inbox "View/Close the announcement" toggle: dropdown chevron + label,
  // underlined on hover/press only.
  showMoreLink: {
    padding: 0,
    background: 'none',
    border: 'none',
    color: darkBlue,
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    '@media (hover:hover)': {
      '&:hover': { textDecoration: 'underline' },
    },
    '&:active': { textDecoration: 'underline' },
    '& svg': {
      transition: 'transform 150ms',
    },
  },
}))

// The maximum number of stats shown before the "Show more" toggle is offered.
const DEFAULT_STAT_COUNT = 3

interface ActivityItemProps {
  activity: ActivityDto
  onDeleteActivity?: (activityId: string) => void
  /** When provided and the activity has duplicates, shows a "Review duplicates" link. */
  onReviewDuplicates?: () => void
  /** Hides the per-item actions menu (used inside the review-duplicates modal for the main item). */
  disableActions?: boolean
}

export const formatDuration = (durationInSeconds: number | null): string => {
  if (!durationInSeconds) return '-'

  const hours = Math.floor(durationInSeconds / 3600)
  const minutes = Math.floor((durationInSeconds % 3600) / 60)
  const seconds = Math.floor(durationInSeconds) % 60

  if (hours > 0) {
    // For durations over 1 hour, don't show seconds
    return `${hours}h ${minutes}m`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  } else {
    return `${seconds}s`
  }
}

// Same shape as formatDuration but renders each h/m/s suffix as <abbr> so the full term
// (hours / minutes / seconds) is exposed to screen readers and on hover. Used by the stats
// row; for the plain-string format keep using formatDuration above.
const renderDuration = (durationInSeconds: number | null, unitClass: string): React.ReactNode => {
  if (!durationInSeconds) return '-'

  const hours = Math.floor(durationInSeconds / 3600)
  const minutes = Math.floor((durationInSeconds % 3600) / 60)
  const seconds = Math.floor(durationInSeconds) % 60

  const hoursAbbr = (
    <abbr className={unitClass} title={t`hours`}>
      h
    </abbr>
  )
  const minutesAbbr = (
    <abbr className={unitClass} title={t`minutes`}>
      m
    </abbr>
  )
  const secondsAbbr = (
    <abbr className={unitClass} title={t`seconds`}>
      s
    </abbr>
  )

  if (hours > 0) {
    return (
      <>
        {hours}
        {hoursAbbr} {minutes}
        {minutesAbbr}
      </>
    )
  } else if (minutes > 0) {
    return (
      <>
        {minutes}
        {minutesAbbr} {seconds}
        {secondsAbbr}
      </>
    )
  }
  return (
    <>
      {seconds}
      {secondsAbbr}
    </>
  )
}

/**
 * Classifies a multi-sport activity from the disciplines present in its legs (transitions and any
 * other types are ignored) so the parent card can show a specific name instead of the generic
 * "Multi-Sport". Falls back to the provided label for combinations we don't have a name for.
 */
const getMultiSportLabel = (legs: ActivityDto[], fallbackLabel: string): string => {
  const hasSwim = legs.some((leg) => leg.activityType === ActivityRowType.SWIMMING)
  const hasBike = legs.some((leg) => leg.activityType === ActivityRowType.CYCLING)
  const hasRun = legs.some(
    (leg) => leg.activityType === ActivityRowType.RUNNING || leg.activityType === ActivityRowType.WALKING,
  )

  if (hasSwim && hasBike && hasRun) return t`Triathlon`
  if (hasBike && hasRun && !hasSwim) return t`Duathlon`
  if (hasSwim && hasRun && !hasBike) return t`Aquathlon`
  if (hasSwim && hasBike && !hasRun) return t`Aquabike`
  return fallbackLabel
}

/**
 * Calculates average speed from distance and duration
 * @param distanceInKm - Distance in kilometers
 * @param durationInSeconds - Duration in seconds
 * @returns Speed in meters per second
 */
const calculateAverageSpeed = (distanceInKm: number | null, durationInSeconds: number | null): number => {
  if (!distanceInKm || !durationInSeconds || distanceInKm === 0 || durationInSeconds === 0) {
    return 0
  }

  // Convert distance to meters and calculate speed in m/s
  const distanceInMeters = distanceInKm * 1000
  return distanceInMeters / durationInSeconds
}

/**
 * Formats timestamp to show relative time for recent activities and formatted date/time for older ones
 * Examples: "Yesterday at 1:05 PM", "Today at 2:30 PM", "2 hours ago", "Dec 15 at 3:45 PM"
 */
const formatDateTime = (timestamp: number): string => {
  if (!timestamp) return ''

  const date = new Date(timestamp)
  const now = dayjs()
  const activityMoment = dayjs(date)

  // Check if the activity is from today or yesterday
  const daysDiff = now.diff(activityMoment, 'day')

  if (daysDiff === 0) {
    // Today - show "Today at [time]"
    const timeString = date.toLocaleTimeString(currentLocale, {
      hour: '2-digit',
      minute: '2-digit',
    })
    return t`Today at ${timeString}`
  } else if (daysDiff === 1) {
    // Yesterday - show "Yesterday at [time]"
    const timeString = date.toLocaleTimeString(currentLocale, {
      hour: '2-digit',
      minute: '2-digit',
    })
    return t`Yesterday at ${timeString}`
  } else if (daysDiff < 7) {
    // Within a week - show relative time like "2 days ago"
    return activityMoment.fromNow()
  } else {
    // Older than a week - show formatted date and time
    const isCurrentYear = now.year() === activityMoment.year()

    if (isCurrentYear) {
      // Same year - show "Dec 15 at 3:45 PM"
      const dateString = date.toLocaleDateString(currentLocale, {
        month: 'short',
        day: 'numeric',
      })
      const timeString = date.toLocaleTimeString(currentLocale, {
        hour: '2-digit',
        minute: '2-digit',
      })
      return t`${dateString} at ${timeString}`
    } else {
      // Different year - show "Dec 15, 2023 at 3:45 PM"
      const dateString = date.toLocaleDateString(currentLocale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
      const timeString = date.toLocaleTimeString(currentLocale, {
        hour: '2-digit',
        minute: '2-digit',
      })
      return t`${dateString} at ${timeString}`
    }
  }
}

/**
 * Formats only the time of day (e.g. "3:45 PM" / "15:45"). Used for multi-sport legs, where the
 * parent card already carries the date, so each leg just needs its start time.
 */
const formatTimeOfDay = (timestamp: number): string => {
  if (!timestamp) return ''
  return new Date(timestamp).toLocaleTimeString(currentLocale, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

// The backend source has the shape "<device> via <provider>". When the device name already
// names the provider ("Garmin fenix 8 Pro via Garmin"), the suffix is pure noise and makes the
// meta line wrap on mobile — drop it. Kept when it adds information ("Strava via Apple Health").
const formatSource = (source?: string | null): string | null | undefined => {
  if (!source) return source
  const match = source.match(/^(.+)\s+via\s+(.+)$/)
  if (!match) return source
  const [, device, provider] = match
  return device.toLowerCase().includes(provider.trim().toLowerCase()) ? device.trim() : source
}

// Label for the card's bottom row: the stored device/source string when present, otherwise the
// integration's name (some providers, e.g. Google Health, store no per-activity source). Manual
// and in-app entries (getServiceProviderLabel -> "Unknown") show nothing.
const sourceOrProviderLabel = (activity: ActivityDto): string | null => {
  const source = formatSource(activity.source)
  if (source) return source
  const providerLabel = getServiceProviderLabel(activity.serviceProvider)
  return providerLabel !== 'Unknown' ? providerLabel : null
}

// Appends the reverse-geocoded "City, Country" to the time line when present, e.g.
// "Today at 10:35 · Tallinn, Estonia". Falsy parts (null / undefined / "" sentinel) are
// skipped. The providing source lives in the card's bottom row next to "Show more".
const renderMeta = (time: string, location?: string | null): string => [time, location].filter(Boolean).join(' · ')

/**
 * Formats activity time - relative for today/yesterday, absolute for older activities
 * Examples: "Yesterday at 1:05 PM", "Today at 2:30 PM", "Dec 15 at 3:45 PM"
 */
const formatActivityTime = (timestamp: number): string => {
  if (!timestamp) return ''

  const activityDate = new Date(timestamp)
  const now = new Date()

  // Time part, localized via the browser's locale (e.g. 24-hour "10:35" for Estonian, not "10:35 AM").
  const timeString = activityDate.toLocaleTimeString(currentLocale, {
    hour: '2-digit',
    minute: '2-digit',
  })

  // Check if it's today or yesterday
  const isToday = activityDate.toDateString() === now.toDateString()
  const isYesterday = activityDate.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString()

  // Use ttag (not dayjs .calendar(), whose default formats are English) so the relative wording and
  // the "at" connector are translated.
  if (isToday) {
    return t`Today at ${timeString}`
  } else if (isYesterday) {
    return t`Yesterday at ${timeString}`
  } else {
    // Use absolute date/time for older activities
    const isCurrentYear = activityDate.getFullYear() === now.getFullYear()
    const dateString = activityDate.toLocaleDateString(currentLocale, {
      month: 'short',
      day: 'numeric',
      // Include the year only when it isn't the current one.
      ...(isCurrentYear ? {} : { year: 'numeric' }),
    })
    return t`${dateString} at ${timeString}`
  }
}

/**
 * Renders one activity's stats. Builds the applicable stats in priority order, shows the first
 * {@link DEFAULT_STAT_COUNT} by default, and offers a "Show more" toggle that reveals the rest
 * inline in the same wrapping row, along with the route chart and map. Its own state means every
 * card and multi-sport leg expands independently.
 */
function ActivityStats({
  activity: a,
  onExpandedChange,
  showSource,
  trailing,
}: {
  activity: ActivityDto
  /** Bubbles the expanded state up so the containing card can show the "open" (blue) border. */
  onExpandedChange?: (expanded: boolean) => void
  /** Shows the providing source after the toggle — on for cards, off for multi-sport legs. */
  showSource?: boolean
  /** Extra bottom-row content after the source (e.g. the "Review duplicates" link). */
  trailing?: React.ReactNode
}) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const userUnits = useSelector((state: IGlobalState) => state.auth.userDto.units || Units.METRIC)
  const routesById = useSelector(getRoutesById)
  const routeLoadingIds = useSelector(getRouteLoadingIds)
  const [expanded, setExpanded] = useState(false)
  // Hovered chart position (elapsed seconds), mirrored on the route map as a moving position marker.
  const [hoverElapsed, setHoverElapsed] = useState<number | null>(null)

  // Sport-relevant gating for the richer Garmin metrics. Cadence is per-sport (bike rpm / run spm /
  // swim strokes-per-minute); elevation is only shown for running and cycling.
  const isCycling = a.activityType === ActivityRowType.CYCLING
  const isRunning = a.activityType === ActivityRowType.RUNNING
  const isSwimming = a.activityType === ActivityRowType.SWIMMING
  const showCadence = isCycling || isRunning || isSwimming
  const cadenceUnit = isCycling ? 'rpm' : 'spm'
  const showElevation = isCycling || isRunning

  // Built in priority order (most important first) so the default three are the ones that matter
  // most for the given sport: distance, primary tempo (pace/speed), time.
  const items: React.ReactNode[] = []

  if (a.distance) {
    items.push(
      <div key="distance" className={classes.statItem}>
        <span className="label">{t`Distance`}</span>
        <span className="value">
          {a.activityType === ActivityRowType.SWIMMING ? (
            <>
              {formatSwimmingDistanceWithUnits(a.distance)}
              <abbr className={classes.unit} title={t`meters`}>
                {getSwimmingDistanceUnit()}
              </abbr>
            </>
          ) : (
            <>
              {formatDistanceWithUnits(a.distance, userUnits)}
              <abbr className={classes.unit} title={userUnits === Units.IMPERIAL ? t`miles` : t`kilometers`}>
                {getDistanceUnit(userUnits)}
              </abbr>
            </>
          )}
        </span>
      </div>,
    )
  }

  // Primary tempo metric — pace for run/swim, avg speed for cycling (prefer the stored value).
  if (isRunning && a.distance && a.duration) {
    items.push(
      <div key="pace" className={classes.statItem}>
        <span className="label">{t`Pace`}</span>
        <span className="value">
          {formatPaceWithUnits(a.distance, a.duration, userUnits)}
          <abbr
            className={classes.unit}
            title={userUnits === Units.IMPERIAL ? t`minutes per mile` : t`minutes per kilometer`}
          >
            {getPaceUnit(userUnits)}
          </abbr>
        </span>
      </div>,
    )
  } else if (isSwimming && a.distance && a.duration) {
    items.push(
      <div key="pace" className={classes.statItem}>
        <span className="label">{t`Pace`}</span>
        <span className="value">
          {formatSwimmingPaceWithUnits(a.distance, a.duration)}
          <abbr className={classes.unit} title={t`minutes per 100 meters`}>
            {getSwimmingPaceUnit()}
          </abbr>
        </span>
      </div>,
    )
  } else if (isCycling && (a.avgSpeed || (a.distance && a.duration))) {
    items.push(
      <div key="avgSpeed" className={classes.statItem}>
        <span className="label">{t`Avg Speed`}</span>
        <span className="value">
          {formatSpeedWithUnits(a.avgSpeed ?? calculateAverageSpeed(a.distance, a.duration), userUnits)}
          <abbr
            className={classes.unit}
            title={userUnits === Units.IMPERIAL ? t`miles per hour` : t`kilometers per hour`}
          >
            {getSpeedUnit(userUnits)}
          </abbr>
        </span>
      </div>,
    )
  }

  if (a.duration) {
    items.push(
      <div key="time" className={classes.statItem}>
        <span className="label">{t`Time`}</span>
        <span className="value">{renderDuration(a.duration, classes.unit)}</span>
      </div>,
    )
  }

  if (a.avgHeartRate) {
    items.push(
      <div key="hr" className={classes.statItem}>
        <span className="label">{t`Avg HR`}</span>
        <span className="value">
          {a.avgHeartRate}
          <abbr className={classes.unit} title={t`beats per minute`}>
            bpm
          </abbr>
        </span>
      </div>,
    )
  }

  if (a.calories) {
    items.push(
      <div key="calories" className={classes.statItem}>
        <span className="label">{t`Calories`}</span>
        <span className="value">
          {a.calories.toLocaleString()}
          <abbr className={classes.unit} title={t`kilocalories`}>
            kcal
          </abbr>
        </span>
      </div>,
    )
  }

  if (showCadence && a.avgCadence) {
    items.push(
      <div key="cadence" className={classes.statItem}>
        <span className="label">{isSwimming ? t`Avg Stroke Rate` : t`Cadence`}</span>
        <span className="value">
          {a.avgCadence}
          <abbr className={classes.unit} title={t`average cadence`}>
            {cadenceUnit}
          </abbr>
        </span>
      </div>,
    )
  }

  if (isCycling && a.maxSpeed) {
    items.push(
      <div key="maxSpeed" className={classes.statItem}>
        <span className="label">{t`Max Speed`}</span>
        <span className="value">
          {formatSpeedWithUnits(a.maxSpeed, userUnits)}
          <abbr
            className={classes.unit}
            title={userUnits === Units.IMPERIAL ? t`miles per hour` : t`kilometers per hour`}
          >
            {getSpeedUnit(userUnits)}
          </abbr>
        </span>
      </div>,
    )
  }

  // Not sourced from Garmin yet; shows when a provider supplies it.
  if (isCycling && a.avgPower) {
    items.push(
      <div key="power" className={classes.statItem}>
        <span className="label">{t`Avg Power`}</span>
        <span className="value">
          {a.avgPower}
          <abbr className={classes.unit} title={t`watts`}>
            W
          </abbr>
        </span>
      </div>,
    )
  }

  if (showElevation && a.elevationGain) {
    items.push(
      <div key="elevGain" className={classes.statItem}>
        <span className="label">{t`Elev. Gain`}</span>
        <span className="value">
          {formatElevation(a.elevationGain, userUnits)}
          <abbr className={classes.unit} title={userUnits === Units.IMPERIAL ? t`feet` : t`meters`}>
            {getElevationUnit(userUnits)}
          </abbr>
        </span>
      </div>,
    )
  }

  if (showElevation && a.elevationLoss) {
    items.push(
      <div key="elevLoss" className={classes.statItem}>
        <span className="label">{t`Elev. Loss`}</span>
        <span className="value">
          {formatElevation(a.elevationLoss, userUnits)}
          <abbr className={classes.unit} title={userUnits === Units.IMPERIAL ? t`feet` : t`meters`}>
            {getElevationUnit(userUnits)}
          </abbr>
        </span>
      </div>,
    )
  }

  if (a.steps) {
    items.push(
      <div key="steps" className={classes.statItem}>
        <span className="label">{t`Steps`}</span>
        <span className="value">{a.steps.toLocaleString()}</span>
      </div>,
    )
  }

  // List responses only flag route availability (hasRoute); the points load lazily on first
  // expand into routesById. DTOs from the duplicates/set-main flows still carry routes inline.
  const routes = a.routes?.length ? a.routes : routesById[a.id] || []
  const hasRoutes = routes.length > 0
  const hasRouteAvailable = hasRoutes || !!a.hasRoute
  const routeLoading = !!routeLoadingIds[a.id]
  const sourceLabel = showSource ? sourceOrProviderLabel(a) : null

  if (items.length === 0 && !hasRouteAvailable && !sourceLabel && !trailing) return null

  const primary = items.slice(0, DEFAULT_STAT_COUNT)
  const extra = items.slice(DEFAULT_STAT_COUNT)
  const hasMore = extra.length > 0
  // The route chart and map also live behind "Show more" — offer the toggle when the activity has
  // GPS routes even if there are no extra stats.
  const hasChart = detectAvailableSeries(routes).length > 0

  return (
    <>
      {items.length > 0 && (
        <div className={classes.activityStats}>
          {primary}
          {expanded && extra}
        </div>
      )}
      {expanded && routeLoading && !hasRoutes && (
        <div style={{ marginTop: '12px' }}>
          <Loader />
        </div>
      )}
      {expanded && hasRoutes && (
        <div style={{ marginTop: '12px' }}>
          <RouteMap routes={routes} activityId={a.id} highlightElapsedSeconds={hoverElapsed} />
        </div>
      )}
      {/* The route time-series chart is part of the expanded view, below the map. Cycling charts
          speed (km/h) instead of pace — pace is a foot-sport measure. */}
      {expanded && hasChart && (
        <RouteStatsChart routes={routes} tempo={isCycling ? 'spd' : 'pace'} onHoverElapsed={setHoverElapsed} />
      )}
      {/* The toggle stays below the expanded content (map + chart), so "Show less" sits at the
          end. The providing source follows after a middot (or stands alone when there is
          nothing to expand). */}
      {(hasMore || hasRouteAvailable || sourceLabel || trailing) && (
        <div className={classes.showMoreRow}>
          {(hasMore || hasRouteAvailable) && (
            <button
              type="button"
              className={classes.showMoreLink}
              aria-expanded={expanded}
              onClick={() => {
                const next = !expanded
                setExpanded(next)
                onExpandedChange?.(next)
                // First expand of a flagged activity: fetch its route points on demand.
                if (next && a.hasRoute && !hasRoutes && !routeLoading) {
                  dispatch(loadActivityRoute({ activityId: a.id }))
                }
              }}
            >
              <DropdownIcon aria-hidden="true" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
              {expanded ? t`Show less` : t`Show more`}
            </button>
          )}
          {sourceLabel && (hasMore || hasRouteAvailable) && (
            <span aria-hidden="true" className={classes.showMoreSource}>
              ·
            </span>
          )}
          {sourceLabel && <span className={classes.showMoreSource}>{sourceLabel}</span>}
          {trailing && (hasMore || hasRouteAvailable || sourceLabel) && (
            <span aria-hidden="true" className={classes.showMoreSource}>
              ·
            </span>
          )}
          {trailing}
        </div>
      )}
    </>
  )
}

export default function ActivityItem({
  activity,
  onDeleteActivity,
  onReviewDuplicates,
  disableActions,
}: ActivityItemProps) {
  const classes = useStyles()

  // Get user units from Redux store
  const userUnits = useSelector((state: IGlobalState) => state.auth.userDto.units || Units.METRIC)

  // Multi-sport parent (e.g. a triathlon): render its legs grouped underneath a header card.
  const childActivities = activity.childActivities || []
  const isMultiSportParent = !!activity.multiSportParent && childActivities.length > 0

  // Renders the workout icon for the given normalized type, or nothing when it can't be mapped.
  const renderIcon = (normalizedType: WorkoutType | null | undefined, size = 16) => {
    const IconComponent = getActivityIconComponent(normalizedType || '')
    return IconComponent ? <IconComponent style={{ width: size, height: size }} /> : null
  }

  // Expanded ("show more") state per activity/leg id, used to paint the open card's border blue —
  // same treatment as an open inbox announcement.
  const [expandedIds, setExpandedIds] = useState<{ [activityId: string]: boolean }>({})

  // Stats/chart/map section for a single activity, shared by the flat card and each nested
  // multi-sport leg. ActivityStats owns its own "show more" state so every card/leg toggles
  // independently.
  const renderStats = (a: ActivityDto, showSource?: boolean, trailing?: React.ReactNode) => (
    <ActivityStats
      activity={a}
      showSource={showSource}
      trailing={trailing}
      onExpandedChange={(expanded) => setExpandedIds((prev) => ({ ...prev, [a.id]: expanded }))}
    />
  )

  // Duplicate review affordance — only shown in the list (when a handler is provided) for
  // activities that have hidden duplicates collapsed under them. Appended to the meta line.
  const renderReviewDuplicates = () =>
    onReviewDuplicates && !!activity.duplicateCount ? (
      <button type="button" className={classes.reviewDuplicatesLink} onClick={onReviewDuplicates}>
        {t`Review duplicates`}
      </button>
    ) : null

  if (isMultiSportParent) {
    // Combined totals are for display only. Distance is stored in km for every leg (swimming
    // included), so the legs can be summed directly. These values are NEVER added into any total —
    // the back-end already excludes the parent from every stats sum.
    const totalDistance = childActivities.reduce((sum, leg) => sum + (leg.distance || 0), 0)
    const totalDuration = childActivities.reduce((sum, leg) => sum + (leg.duration || 0), 0)

    // Label transition legs T1, T2, … in chronological order (legs arrive sorted by start time);
    // other legs keep their normal workout label.
    let transitionCount = 0
    const legLabels = childActivities.map((leg) => {
      if (leg.normalizedType === 'transition') {
        transitionCount += 1
        return `T${transitionCount}`
      }
      return getWorkoutLabel(leg.normalizedType)
    })

    return (
      <div className={classes.activityItem}>
        <div className={classes.activityHeader}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div className={classes.activityContent}>
              <Typography className={classes.activityTitle}>
                {renderIcon(activity.normalizedType)}
                {getMultiSportLabel(childActivities, getWorkoutLabel(activity.normalizedType))}
              </Typography>
              <Typography className={classes.activityMeta}>
                {renderMeta(formatActivityTime(activity.startTime), activity.locationName)}
              </Typography>
            </div>
          </div>

          {!disableActions && <ActivityActions activity={activity} onDeleteActivity={onDeleteActivity} />}
        </div>

        {/* Combined totals across all legs (display only) */}
        {(totalDistance || totalDuration) && (
          <div className={classes.activityStats}>
            {totalDistance ? (
              <div className={classes.statItem}>
                <span className="label">{t`Distance`}</span>
                <span className="value">
                  {formatDistanceWithUnits(totalDistance, userUnits)}
                  <abbr className={classes.unit} title={userUnits === Units.IMPERIAL ? t`miles` : t`kilometers`}>
                    {getDistanceUnit(userUnits)}
                  </abbr>
                </span>
              </div>
            ) : null}
            {totalDuration ? (
              <div className={classes.statItem}>
                <span className="label">{t`Time`}</span>
                <span className="value">{renderDuration(totalDuration, classes.unit)}</span>
              </div>
            ) : null}
          </div>
        )}

        {/* Individual legs (swim / bike / run / transition) */}
        <div className={classes.legList}>
          {childActivities.map((leg, index) => (
            <div
              key={leg.id}
              className={classes.legItem}
              style={expandedIds[leg.id] ? { borderColor: primaryBlue } : undefined}
            >
              <div className={classes.legHeader}>
                <div>
                  <Typography className={classes.legTitle}>
                    {renderIcon(leg.normalizedType, 14)}
                    {legLabels[index]}
                  </Typography>
                  <Typography className={classes.activityMeta}>
                    {renderMeta(formatTimeOfDay(leg.startTime), leg.locationName)}
                  </Typography>
                </div>
                {/* Each leg is a real activity, so keep its own delete action. */}
                {!disableActions && <ActivityActions activity={leg} onDeleteActivity={onDeleteActivity} />}
              </div>
              {renderStats(leg)}
            </div>
          ))}
        </div>

        {/* The providing source and duplicates link, in the same bottom-row spot as on regular cards. */}
        {(sourceOrProviderLabel(activity) || renderReviewDuplicates()) && (
          <div className={classes.showMoreRow}>
            {sourceOrProviderLabel(activity) && (
              <span className={classes.showMoreSource}>{sourceOrProviderLabel(activity)}</span>
            )}
            {sourceOrProviderLabel(activity) && renderReviewDuplicates() && (
              <span aria-hidden="true" className={classes.showMoreSource}>
                ·
              </span>
            )}
            {renderReviewDuplicates()}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={classes.activityItem} style={expandedIds[activity.id] ? { borderColor: primaryBlue } : undefined}>
      <div className={classes.activityHeader}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          {/* Activity information */}
          <div className={classes.activityContent}>
            <Typography className={classes.activityTitle}>
              {renderIcon(activity.normalizedType)}
              {getWorkoutLabel(activity.normalizedType)}
            </Typography>
            <Typography className={classes.activityMeta}>
              {renderMeta(formatActivityTime(activity.startTime), activity.locationName)}
            </Typography>
          </div>
        </div>

        {/* Activity actions */}
        {!disableActions && <ActivityActions activity={activity} onDeleteActivity={onDeleteActivity} />}
      </div>

      {/* Only show stats if we have any data to display */}
      {renderStats(activity, true, renderReviewDuplicates())}
    </div>
  )
}
